// combatpro/initiative/initiativeEngine.js
//
// Combat PRO - Initiative Engine (Stage 37: pure initiative logic).
//
// Pure, UI-independent initiative logic extracted from the initiative runtime in
// battletracker.html (the read-only master spec). This module knows nothing
// about React, components, context, history, rendering, or the DOM. It contains
// only deterministic initiative mathematics: ordering, position math, round
// advancement, and the initiative ability modifier. It receives the monster
// array and primitive values as arguments; it does NOT read globals and does NOT
// own any state, so it carries no Monster Runtime or Encounter Runtime
// dependency. It is reusable by the later initiative-integration stage once
// Monster Runtime is migrated.
//
// SCOPE (Stage 37, Option A): pure slice only. No React wiring is performed this
// stage. The legacy nextCombatant / prevCombatant / rollInitiative /
// rollInitiativeForAll functions are coupled to Monster Runtime (monsters[i]
// fields, switchMonster), Encounter Runtime (updateEncounterSummary, saveState,
// sendActionToCloud), Lair/Legendary systems, and imperative DOM (active-panel
// lookup, badge/notification rendering). Those couplings are intentionally NOT
// reproduced here; only the deterministic kernel each one wraps is provided as a
// pure function. Interfaces to out-of-scope systems are preserved by the future
// integration layer, not by this engine.
//
// Source mapping (battletracker.html):
//   - getInitiativeOrder()         line 5458. Pure logic reproduced as
//                                  getInitiativeOrder(monsters, activeMonsterCount):
//                                  slice(0, activeMonsterCount), map to
//                                  { index, initiative: m.initiative || 0 },
//                                  filter initiative > 0, sort highest-first
//                                  (b.initiative - a.initiative). The index is the
//                                  position within the sliced active range, exactly
//                                  as the source produced it.
//   - nextCombatant() index math   line 5465. Reproduced as
//                                  nextCombatantStep(positionInOrder, orderLength):
//                                  next = positionInOrder + 1; if next >= orderLength
//                                  then next = 0 and roundDelta = +1, else
//                                  roundDelta = 0. The source's not-found case
//                                  (positionInOrder === -1) yields next = 0 here too
//                                  (-1 + 1), matching behaviour. DOM lookup, lair
//                                  trigger, legendary reset, switchMonster, encounter
//                                  update, save, and cloud are NOT part of this
//                                  pure step.
//   - prevCombatant() index math   line 5523. Reproduced as
//                                  prevCombatantStep(positionInOrder, orderLength,
//                                  currentRound): the source coerces a not-found
//                                  position (-1) to 0 before decrementing, so callers
//                                  pass the coerced position; prev = positionInOrder - 1;
//                                  if prev < 0 then prev = orderLength - 1 and
//                                  roundDelta = (currentRound > 1 ? -1 : 0), else
//                                  roundDelta = 0. Note the asymmetry with next: prev
//                                  only decrements the round when currentRound > 1.
//   - nextRound() round step       line 7943. Round kernel reproduced as
//                                  advanceRound(currentRound): currentRound + 1.
//                                  Snapshot, legendary reset, panel update, save, and
//                                  cloud are NOT part of this pure step.
//   - prevRound() round step       line 7965. Reproduced as
//                                  retreatRound(currentRound): currentRound > 1 ?
//                                  currentRound - 1 : currentRound (guarded, never
//                                  below 1), matching the source guard.
//   - rollInitiative() kernel      line 6534. The deterministic kernel is
//                                  computeInitiativeTotal(d20Roll, dexScore):
//                                  d20Roll + getAbilityModifier(dexScore). The d20
//                                  itself comes from the Dice Runtime
//                                  (diceEngine.rollDie(20)) at the integration layer,
//                                  not here; this function takes the already-rolled
//                                  value so the engine stays free of randomness when a
//                                  caller wants determinism. A convenience
//                                  rollInitiativeTotal(rollDie, dexScore) is also
//                                  provided for callers that pass the Dice engine's
//                                  rollDie function in (dependency injected, so this
//                                  module still imports nothing).
//   - getAbilityModifier()         line ~7959. Reproduced as
//                                  getAbilityModifier(score): Math.floor((score - 10) / 2).
//                                  This is shared D&D math the initiative roll needs; it
//                                  is duplicated here as a pure helper rather than
//                                  imported from Monster Runtime (which is not migrated).
//
// FORBIDDEN here and absent: React, components, context, DOM, getElementById,
// innerHTML, document, window, localStorage, API, fetch, websocket, AI, history,
// rendering, side effects. No optional chaining. No em dash.

// Standard D&D 5e ability modifier. Pure. Matches the source getAbilityModifier.
export function getAbilityModifier(score) {
  return Math.floor((score - 10) / 2);
}

// Build the initiative order from the monster array and the active count.
// Returns entries { index, initiative } for active monsters whose initiative is
// greater than 0, sorted highest initiative first. The index is the position
// within the active (sliced) range, matching the source.
export function getInitiativeOrder(monsters, activeMonsterCount) {
  var active = [];
  var limit = activeMonsterCount;
  if (limit > monsters.length) { limit = monsters.length; }
  for (var i = 0; i < limit; i++) {
    var m = monsters[i];
    var initiative = (m && m.initiative) ? m.initiative : 0;
    if (initiative > 0) {
      active.push({ index: i, initiative: initiative });
    }
  }
  active.sort(function (a, b) { return b.initiative - a.initiative; });
  return active;
}

// Compute the next combatant position and whether the round advances. Mirrors
// nextCombatant's index math: position + 1, wrapping to 0 with a +1 round delta.
// A not-found position of -1 yields index 0 with no wrap (matching -1 + 1 = 0).
export function nextCombatantStep(positionInOrder, orderLength) {
  var next = positionInOrder + 1;
  var roundDelta = 0;
  if (next >= orderLength) {
    next = 0;
    roundDelta = 1;
  }
  return { index: next, roundDelta: roundDelta };
}

// Compute the previous combatant position and whether the round retreats.
// Mirrors prevCombatant's index math: callers pass the source-coerced position
// (a not-found -1 coerced to 0 before this call). position - 1, wrapping to
// orderLength - 1 with a -1 round delta ONLY when currentRound > 1, matching the
// source guard. Note the asymmetry with nextCombatantStep.
export function prevCombatantStep(positionInOrder, orderLength, currentRound) {
  var prev = positionInOrder - 1;
  var roundDelta = 0;
  if (prev < 0) {
    prev = orderLength - 1;
    roundDelta = currentRound > 1 ? -1 : 0;
  }
  return { index: prev, roundDelta: roundDelta };
}

// Round advancement kernel from nextRound: round + 1.
export function advanceRound(currentRound) {
  return currentRound + 1;
}

// Round retreat kernel from prevRound: guarded so the round never drops below 1.
export function retreatRound(currentRound) {
  return currentRound > 1 ? currentRound - 1 : currentRound;
}

// Deterministic initiative total: a supplied d20 roll plus the DEX modifier.
// Mirrors rollInitiative's total (d20Roll + dexMod) without performing the roll,
// keeping this function free of randomness for deterministic callers.
export function computeInitiativeTotal(d20Roll, dexScore) {
  return d20Roll + getAbilityModifier(dexScore);
}

// Convenience roller: takes a rollDie function (dependency injected, e.g. the
// Dice Runtime's diceEngine.rollDie) and the DEX score, rolls a d20, and returns
// the initiative total. This module imports nothing; the dice dependency is
// passed in by the caller so the engine boundary stays clean.
export function rollInitiativeTotal(rollDie, dexScore) {
  var d20Roll = rollDie(20);
  return computeInitiativeTotal(d20Roll, dexScore);
}
